import { useCallback } from 'react';

import { ENDPOINTS, type ListClauseMiningDryRunsResponse } from '@clm/shared-types';

import { listClauseMiningDryRuns } from '../../api/clause-mining-dry-runs';
import { PIPELINE_FORBIDDEN_COPY } from '../pipeline-status-chips';
import { PanelProblem } from '../quality/panel';
import { usePanel } from '../quality/panel-state';
import { ActiveDryRunCard } from './ActiveDryRunCard';
import { DryRunHistory } from './DryRunHistory';
import { PendingDryRuns } from './PendingDryRuns';
import { RequestDryRunForm } from './RequestDryRunForm';

/**
 * The **Clause mining** section on the Pipeline page — CLM-103 (mine-clauses
 * first slice; match-clauses added in the completion slice, sharing this
 * section, the same request/approve/run-now/history shape and the same
 * `clm.clause_mining_dry_run` queue).
 *
 * mine-clauses and match-clauses runs from the web app the way the extraction
 * pipeline does (CLM-83's Runs section, just above this one on the page) —
 * request → approve → run in the API's own background worker, one at a time
 * per worker, each with a recorded history — but **dry run only**: segment,
 * cluster, describe and match can all be read here; nothing here writes a
 * paragraph, a cluster, a match, or spends a model call. `mine-clauses.yml`
 * and `match-clauses.yml`'s stage dropdowns remain the only way to run a
 * **live** stage, and that is deliberate, not a placeholder:
 *
 *  - a dry run computes and prints the plan for every stage — including
 *    describe — without calling the extraction provider, so there is no
 *    spend for an approval step to gate here;
 *  - a live run needs a stage-sequencing safeguard this slice does not build
 *    (today that safeguard is operator friction: reading segment's and
 *    cluster's output before choosing describe in the workflow's dropdown —
 *    a UI that let you dispatch a live run stage-by-stage would have to
 *    preserve that "read before you pay" property, not just gate *who* may
 *    click run);
 *  - the CLM-101 batch-statement-timeout property and the untracked
 *    double-dispatch gap between an API worker and this break-glass workflow
 *    both need a real design before a live path ships.
 *
 * One read, `GET /v1/clause-mining/dry-runs`, on `contract:read` (everyone);
 * the three components below act through the API and call `reload()` — no
 * row is patched in place.
 */

const PANEL = 'clause mining dry runs';
const ENDPOINT_PATH = `${ENDPOINTS['list-clause-mining-dry-runs'].method} ${ENDPOINTS['list-clause-mining-dry-runs'].path}`;

export function ClauseMiningDryRunsSection() {
  const { state, reload } = usePanel<ListClauseMiningDryRunsResponse>((abort) => listClauseMiningDryRuns(abort), []);

  const finished = useCallback(() => {
    reload();
  }, [reload]);

  return (
    <section className="card" aria-labelledby="clause-mining-dry-runs-heading">
      <h3 id="clause-mining-dry-runs-heading">Clause mining</h3>
      <p className="mut">
        mine-clauses dry runs — segment, cluster and describe — and match-clauses dry runs are requested here,
        approved by an admin, and run by the API&rsquo;s own workers under their identity, exactly like the Runs
        section above. Every run here is a dry run: it reads and reports, writes nothing and calls no model. A
        live (writing, spending) run stays on the <code>mine-clauses</code> or <code>match-clauses</code> GitHub
        Actions workflow, which still enforces reading each stage&rsquo;s output before dispatching the next.
      </p>

      <RequestDryRunForm onDone={reload} />

      {state.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading {PANEL}…
        </p>
      ) : null}
      {state.kind === 'problem' ? (
        <PanelProblem
          panel={PANEL}
          state={state}
          endpointPath={ENDPOINT_PATH}
          onRetry={reload}
          forbiddenCopy={PIPELINE_FORBIDDEN_COPY}
        />
      ) : null}
      {state.kind === 'ready' ? (
        <>
          {state.response.active === null ? null : (
            <ActiveDryRunCard key={state.response.active.clause_mining_dry_run_id} run={state.response.active} onFinished={finished} />
          )}
          <h4>Pending</h4>
          <PendingDryRuns runs={state.response.pending} onDone={reload} />
          <h4>History</h4>
          <DryRunHistory runs={state.response.history} />
        </>
      ) : null}
    </section>
  );
}
